import { useState, useEffect, useRef } from 'react'
import { IRevenueSpendFilters, IFilterConfig } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

type IUseFiltersRevenueSpendPage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	revenueSpendFiltersConfig: IFilterConfig<keyof IRevenueSpendFilters>[];
	defaultFiltersValue: IRevenueSpendFilters;
	initialFiltersValue: IRevenueSpendFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IRevenueSpendFilters | null)) => (IRevenueSpendFilters | null)) | IRevenueSpendFilters | null)) => void;
}

const REVENUE_SPEND_FILTERS_DEFAULT: IRevenueSpendFilters = {
	accountId: '',
	'account.auth.login': '',
	'account.extra.keitaroLogin': '',
	'role.inGroup': '',
	agencyId: '',
	'agency.name': '',
	at: ['', ''],
	'amount': ['', '']
}


export const useRevenueSpendFiltersPage = (buyer: number): IUseFiltersRevenueSpendPage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)
	const [buyerPrev, setBuyerPrev] = useState<number>(buyer)

	const profile = useAtomValue(state.profileAtom)
	const isBuyer = useAtomValue(state.isBuyerAtom)

	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IRevenueSpendFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const revenueSpendFiltersConfig: IFilterConfig<keyof IRevenueSpendFilters>[] = [
		{
			name: 'accountId',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_account_id),
			type: EFilterType.input,
			attr: { type: 'number' },
			operator: EFilterOperator.isIntLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.auth.login',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_account),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.extra.keitaroLogin',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_kt_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.inGroup',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_account_in_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'agencyId',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_agency_id),
			type: EFilterType.input,
			attr: { type: 'number' },
			operator: EFilterOperator.isIntLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'agency.name',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_agency),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'at',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_date),
			type: EFilterType.dateRange,
			operator: EFilterOperator.isBetween,
			styles: { width: '280px' }
		},
		{
			name: 'amount',
			label: t(ALL_LANG_KEY.revenue_spend_list_table.column_amount),
			type: EFilterType.numberRange,
			attr: { type: 'number' },
			operator: EFilterOperator.isBetween,
			styles: { width: '350px' },
			placeholder: t(ALL_LANG_KEY.revenue_spend_crud_modal.field_amount_placeholder)
		}
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		revenueSpendFiltersConfig,
		REVENUE_SPEND_FILTERS_DEFAULT
	)

	useEffect(() => {
		let _userFilters = userFilters
		//console.log('hook.useRevenueSpendFiltersPage useEffect::START:: ', { send, buyer, buyerPrev, userFilters, _userFilters, filter })
		if (send && buyerPrev !== buyer) {
			// reset filters [RFC] onBuyer (menu tabs) changed
			setBuyerPrev(buyer)
			//console.log('!!! clear main filter:: send && buyerPrev !== buyer !!!')
			setFilter([]) // clear main filter with mixins
			_userFilters = REVENUE_SPEND_FILTERS_DEFAULT
			setUserFilters(REVENUE_SPEND_FILTERS_DEFAULT)
			setClearChildFilters(prev => prev + 1) // clearAllChildFilters command-flag
		}

		if (!!_userFilters) {
			setSend(true)
			//console.log('hook.useFiltersOfferScopePage useEffect::MIDDLE:: ', { userFilters, _userFilters, filter })
			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				_userFilters,
				revenueSpendFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				if (!!isBuyer && `${isBuyer}` !== prev.get('buyer')) {
					prev.set('buyer', encodeURIComponent(buyer))
				}

				return prev
			})

			if (buyer) {
				newFilter.push([`accountId${EFilterOperator.isEqual}`, buyer])
			}

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			//console.log('hook.useRevenueSpendFiltersPage:: useEffect::END:: ', { userFilters, _userFilters, filter, newFilter, newFilterStr })
		}
	}, [userFilters, buyer])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueSpendFiltersConfig,
		defaultFiltersValue: REVENUE_SPEND_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}